/**
 * 预算内核 —— 唯一预算花费切片 · 窗口与时间进度 · 判档 · J3「本月剩余可花」
 *
 * 依据：规格 **§5**（预算）· **§12**（J3 可花）· 架构 **§5.5** · `DR-011`（唯一预算函数）·
 * `DR-010`（J3）· **G 域 PRD §4.2–§4.6**（窗口半开区间 / 派生指标 / 判档规则）。
 *
 * ## 三条纪律
 *
 * 1. 🔴 **唯一预算花费函数** —— 本文件的 `budgetSpentFrom()` **就是**规格唯一「支出」口径
 *    在「给定窗口 + 给定分类」上的**切片**：它**不自己算聚合**，只从
 *    `composeExpense()` 的分组结果里**按键取数**（规格 §5「禁止任何模块自写预算聚合」）。
 * 2. 🔴 **绝不按 `__total__` 查聚合** —— 总预算花费 = 窗口内**全部**支出分类之和；
 *    占位键只用于存储（规格 §5 / G 域 §4.3）。
 * 3. 🔴 **待缴·未付计入「已用」但必须标注** —— 不为它开口径例外，
 *    卡面显式标注「含待缴 ¥X 未付」（仅 X>0 时显示）；**档位判定仍按含待缴的数**。
 *
 * ## 与「预算档位快照」的分工（防双实现，规格 §5）
 *
 * 预算域**只算档位并落 `budget_status_snapshot`**，**不推送、不发事件**；
 * 跨档判定与推送由**提醒中心（H 域）拉取快照比对**（仅**档位升级**触发）。
 */

import type { Cents, RatioE4 } from '../types/brand.js';
import { BudgetThreshold, BudgetTotalKey } from '../constants/index.js';
import { BudgetLevel, BudgetPeriod } from '../enums/plan.js';
import { CategoryKind } from '../enums/platform.js';
import { divideExact, ratioE4, ratioE4Of, sumCents, toCents } from './money.js';
// 🔴 日历工具**唯一实现**在 `calendar.ts` —— 本文件不再自带一份（铁律 24：同一机制禁两处各答一遍）
import {
  QUARTER_FIRST_MONTH,
  addDays,
  addMonths,
  clampInt,
  daysBetween,
  monthEndExclusive,
  monthStart,
  quarterStart,
  yearStart,
} from './calendar.js';
import type { CategoryExpense } from './expense.js';

// ═══════════════════════ 1. 窗口（半开区间 [start, end)） ═══════════════════════

/**
 * 预算窗口 —— 🔴 **一律半开区间 `[start, end)`**（G 域 §4.2：杜绝边界双计）。
 *
 * ⚠️ **按账本时区计算**（复用 A 域时区口径）。本内核用 `YYYY-MM-DD` 字符串
 * 承载"账本本地日历日"，并以 **UTC 解析**做天数运算 —— 因为字符串已是"本地日"，
 * 再套一次本地时区会引入双重偏移。
 */
export interface BudgetWindow {
  /** 含 */
  start: string;
  /** 不含 */
  end: string;
  totalDays: number;
  elapsedDays: number;
}

/** `event` 预算的窗口状态（G 域 §4.2） */
export type BudgetEventState = 'notStarted' | 'ongoing' | 'ended';

/**
 * 算预算窗口（含派生天数）。
 *
 * @param period 周期（`monthly` / `quarterly` / `yearly` / `event`）
 * @param today  账本本地日历日（`YYYY-MM-DD`）
 * @param eventRange `event` 必填的起止（`endDate` 为**最后一天**，本函数内部转半开区间）
 */
export function composeBudgetWindow(
  period: BudgetPeriod,
  today: string,
  eventRange?: { startDate: string; endDate: string },
): BudgetWindow {
  let start: string;
  let end: string;

  if (period === BudgetPeriod.Event) {
    if (eventRange === undefined) {
      throw new RangeError('event 预算必须提供 startDate / endDate');
    }
    start = eventRange.startDate;
    // 🔴 `endDate` 是"最后一天"（含）⇒ 半开区间的 end = 次日
    end = addDays(eventRange.endDate, 1);
  } else if (period === BudgetPeriod.Yearly) {
    start = yearStart(today);
    end = addMonths(start, 12);
  } else if (period === BudgetPeriod.Quarterly) {
    start = quarterStart(today);
    end = addMonths(start, 3);
  } else {
    // monthly（默认）
    start = monthStart(today);
    end = monthEndExclusive(today);
  }

  const totalDays = daysBetween(start, end);
  const elapsedDays = clampInt(daysBetween(start, today), 0, totalDays);

  return { start, end, totalDays, elapsedDays };
}

/** 由窗口起止与"今天"判 `event` 状态（`notStarted` / `ongoing` / `ended`） */
export function composeEventState(
  window: BudgetWindow,
  today: string,
): BudgetEventState {
  const t = daysBetween(window.start, today);
  if (t < 0) return 'notStarted';
  if (daysBetween(today, window.end) <= 0) return 'ended';
  return 'ongoing';
}

// ═══════════════════════ 2. 唯一预算花费切片 ═══════════════════════

/**
 * 🔴 **唯一预算花费函数** —— 从唯一「支出」口径的**分组结果**里取数。
 *
 * - `categoryId` 为 `null` 或 {@link BudgetTotalKey} ⇒ **总预算花费** =
 *   窗口内**全部**支出分类 `net` 之和（⚠️ **绝不 `breakdown.get('__total__')`** —— 那样得 0）；
 * - 否则 ⇒ 该分类的 `net`（不存在则 0）。
 *
 * ⚠️ **它不做任何聚合计**：`breakdown` 必须来自 `composeExpense()` /
 * `composeExpenseByCategory()` —— 这是「同一实现的分组版」用法，
 * 不是第二个公式（规格 §5 红线）。
 */
export function budgetSpentFrom(
  breakdown: ReadonlyMap<string, CategoryExpense>,
  categoryId: string | null,
): Cents {
  if (categoryId === null || categoryId === BudgetTotalKey) {
    return sumCents([...breakdown.values()].map((c) => c.net));
  }
  return breakdown.get(categoryId)?.net ?? (0 as Cents);
}

// ═══════════════════════ 3. 派生指标 + 判档 ═══════════════════════

export interface BudgetProgressInput {
  /** 预算额 */
  amount: Cents;
  /** 已用（**含**待缴·未付） */
  spent: Cents;
  /** 其中「待缴·未付」部分（仅用于标注，不改变口径） */
  unpaidPending?: Cents;
  /** 窗口总天数 */
  totalDays: number;
  /** 已过天数 */
  elapsedDays: number;
  period: BudgetPeriod;
  /** 预算被停用 ⇒ 不评估 */
  enabled?: boolean;
  /** `event` 的状态（非 `event` 传 `null`/省略） */
  eventState?: BudgetEventState | null;
}

export interface BudgetProgress {
  amount: Cents;
  spent: Cents;
  /** 🔴 `> 0` 时 UI 才标注「含待缴 ¥X 未付」 */
  unpaidPending: Cents;
  /** 🔴 `amount − spent`，**不截断**（可为负） */
  remaining: Cents;
  /** 已用%（`E4`） */
  usedE4: RatioE4;
  /** 时间进度（`E4`） */
  timeProgressE4: RatioE4;
  /** 偏离 = 已用% − 时间进度%（可为负 ⇒ "偏慢"） */
  deviationE4: number;
  /** 线性外推预计；🔴 `timeProgress < 10%` 或为 0 时 = `null`（不显示） */
  projectedEnd: Cents | null;
  level: BudgetLevel;
  /**
   * 本次**是否参与了判档** —— `false` 表示（停用 / `amount ≤ 0` / `event` 未开始或已结束）
   * 调用方**不应落快照**（`event` 已结束须**保留最终档位、不再评估**）。
   */
  evaluated: boolean;
}

/**
 * 判档（G 域 §4.6，移植 v1 `budgetStatus`）：
 *
 * | 周期 | `over`（红） | `warn`（黄） |
 * |---|---|---|
 * | `monthly` | 已用% ≥ **100%** | 已用% ≥ **80%** |
 * | `quarterly`/`yearly`/`event` | 已用% > 时间进度% **+30%** | 已用% > 时间进度% **+10%** |
 * | 全部周期 | 外推 `projectedEnd > amount` ⇒ **至少 `warn`** | |
 *
 * 判定顺序：**先 `over` 再 `warn`**，否则 `safe`（`over` 优先）。
 */
export function judgeBudgetLevel(a: {
  period: BudgetPeriod;
  usedE4: RatioE4;
  timeProgressE4: RatioE4;
  projectedEnd: Cents | null;
  amount: Cents;
  enabled?: boolean;
  eventState?: BudgetEventState | null;
}): BudgetLevel {
  // 不评估的情形 ⇒ 返回 safe（调用方据 `evaluated` 判断是否落快照）
  if (a.enabled === false) return BudgetLevel.Safe;
  if ((a.amount as number) <= 0) return BudgetLevel.Safe;
  if (a.eventState === 'notStarted' || a.eventState === 'ended') return BudgetLevel.Safe;

  const used = a.usedE4 as number;
  const tp = a.timeProgressE4 as number;

  let level: BudgetLevel = BudgetLevel.Safe;

  if (a.period === BudgetPeriod.Monthly) {
    if (used >= BudgetThreshold.OverE4) level = BudgetLevel.Over;
    else if (used >= BudgetThreshold.WarnE4) level = BudgetLevel.Warn;
  } else {
    if (used > tp + BudgetThreshold.DeviationOverE4) level = BudgetLevel.Over;
    else if (used > tp + BudgetThreshold.DeviationWarnE4) level = BudgetLevel.Warn;
  }

  // 外推兜底：还没判到档，但按当前节奏会超 ⇒ 至少 warn
  if (level === BudgetLevel.Safe && a.projectedEnd !== null) {
    if ((a.projectedEnd as number) > (a.amount as number)) level = BudgetLevel.Warn;
  }

  return level;
}

/** 一次算齐：派生指标 + 档位（消费方只调这一个） */
export function composeBudgetProgress(input: BudgetProgressInput): BudgetProgress {
  const amount = input.amount;
  const spent = input.spent;
  const unpaidPending = input.unpaidPending ?? (0 as Cents);

  const usedE4 = ratioE4(spent, amount);
  const timeProgressE4 = ratioE4Of(input.elapsedDays, input.totalDays);

  // 外推：`budgetSpent ÷ timeProgress`；🔴 保护 `timeProgress < 10%` 与除零
  const tp = timeProgressE4 as number;
  let projectedEnd: Cents | null = null;
  if (tp >= BudgetThreshold.ExtrapolateMinTimeProgressE4) {
    const scaled = (spent as number) * 10000;
    projectedEnd = toCents(divideExact(scaled, tp)) as Cents;
  }

  const eventState = input.eventState ?? null;
  const enabled = input.enabled !== false;

  const level = judgeBudgetLevel({
    period: input.period,
    usedE4,
    timeProgressE4,
    projectedEnd,
    amount,
    enabled,
    eventState,
  });

  // 是否参与判档：停用 / 非正预算 / event 未开始或已结束 ⇒ 不落快照
  const evaluated =
    enabled
    && (amount as number) > 0
    && eventState !== 'notStarted'
    && eventState !== 'ended';

  return {
    amount,
    spent,
    unpaidPending,
    remaining: toCents((amount as number) - (spent as number)) as Cents,
    usedE4,
    timeProgressE4,
    deviationE4: usedE4 - timeProgressE4,
    projectedEnd,
    level,
    evaluated,
  };
}

// ═══════════════════════ 4. J3「本月剩余可花」 ═══════════════════════

/**
 * 🔴 `本月剩余可花 = 预算 − 已花 − 必付未付`（规格 §12 · `DR-010`）。
 *
 * - **已花** = 唯一预算花费函数（{@link budgetSpentFrom}）；
 * - **必付未付** = `composeUpcoming()` 的同一份聚合（**禁自算**）；
 * - **无预算** ⇒ `noBudget`：UI 显示**引导**、**不显示数字**；
 * - 结果为负 ⇒ `overspent`：显示「**已透支 ¥X**」，🔴 **不截断为 0**。
 *
 * ⚠️ **与 F3「本次分配后可花」分名分口径、禁共用函数**（规格 §12）——
 * 本函数**只服务 J3**。
 */
export type SpendableResult =
  | { kind: 'noBudget' }
  | { kind: 'available'; value: Cents }
  | { kind: 'overspent'; overspentBy: Cents };

export function composeSpendable(input: {
  /** `null` = 该账本/窗口无预算 */
  budgetAmount: Cents | null;
  /** 已花（唯一预算花费函数的结果） */
  spent: Cents;
  /** 必付未付（`composeUpcoming` 的总额） */
  upcomingTotal: Cents;
}): SpendableResult {
  if (input.budgetAmount === null) {
    return { kind: 'noBudget' };
  }
  const value =
    (input.budgetAmount as number) - (input.spent as number) - (input.upcomingTotal as number);
  if (value < 0) {
    return { kind: 'overspent', overspentBy: toCents(-value) as Cents };
  }
  return { kind: 'available', value: toCents(value) as Cents };
}

// ═══════════════════════ 5. 窗口标识（快照的幂等键组成部分） ═══════════════════════

/**
 * **窗口标识** —— `budget_status_snapshot.window` 的**唯一产生处**。
 *
 * 依据：架构 §4.2（该列注释即「`YYYY-MM` / `YYYY-Qn` / `YYYY` / event 的自定义键」）·
 * G 域 PRD §4.10（H 域按「账本 + 预算 + 窗口」比对快照 ⇒ **同一窗口必须在两轮之间稳定**）。
 *
 * 🔴 **为什么必须在内核**：H 域要拿它做幂等哈希（`hash(ledgerId + budgetId + window + 档位)`）。
 * 若服务端在这一处拼、H 域在那一处拼，`2026-Q4` 与 `2026Q4` 就是**两个窗口** ⇒
 * "同窗口只推一次"静默失效（重复推送，反指标本应恒为 0）。
 *
 * ⚠️ `event` 用**闭区间**书写（`2026-10-01~2026-10-07`）—— 给排查的人看的，
 * 而窗口本身仍是半开区间 `[start, end)`。
 */
export function budgetWindowKey(period: BudgetPeriod, window: BudgetWindow): string {
  switch (period) {
    case BudgetPeriod.Yearly:
      return window.start.slice(0, 4);
    case BudgetPeriod.Quarterly: {
      const month = Number(window.start.slice(5, 7));
      return `${window.start.slice(0, 4)}-Q${QUARTER_OF_MONTH[month - 1] ?? 1}`;
    }
    case BudgetPeriod.Event:
      return `${window.start}~${addDays(window.end, -1)}`;
    default:
      return window.start.slice(0, 7);
  }
}

/**
 * 月（1–12）→ 季序（1–4）—— 🔴 **由 `QUARTER_FIRST_MONTH` 派生**，不手抄第二份季度定义。
 *
 * ⚠️ 用表而不是 `Math.floor((m − 1) / 3)`：本项目 eslint 把 `Math.floor` 与 `/` 全项目封死
 * （定点白名单之外禁除法）—— 而"季度"本就是**离散查表**语义，查表比除法更贴切。
 */
const QUARTER_OF_MONTH: readonly number[] = (() => {
  const out: number[] = [];
  for (let q = 1; q <= 4; q += 1) {
    const first = QUARTER_FIRST_MONTH[(q - 1) * 3] ?? 1;
    out[first - 1] = q;
    out[first] = q;
    out[first + 1] = q;
  }
  return out;
})();

// ═══════════════════════ 6. 建 / 改的形态校验 ═══════════════════════

/**
 * 建 / 改预算时可能给出的中文拒绝文案 —— **唯一文案源**。
 *
 * 🔴 服务端 `400` 的 `message` 与端侧表单的提示**必须逐字相同**（㉛ 批的「对字」纪律）
 * ⇒ 文案只能有一处，谁也不许在别处再写一遍。
 */
export const BUDGET_MESSAGES = {
  amount: '预算金额必须大于 0。',
  incomeCategory: '预算只能设在支出分类上（收入分类不属于预算范围）。',
  unknownCategory: '分类不存在或不属于当前账本。',
  eventRangeRequired: '事件预算必须填写起止日期。',
  eventRangeReversed: '事件预算的结束日期不能早于开始日期。',
  duplicate: '该分类已有同周期的预算，已跳过。',
} as const;

/** {@link checkBudgetShape} 的输入 —— 分类大类由**调用方查好传入**（内核不认识数据库） */
export interface BudgetShapeInput {
  period: BudgetPeriod;
  /** 分类 id，或总预算占位键 {@link BudgetTotalKey} */
  categoryId: string;
  /**
   * 该分类的大类：
   * - `__total__` ⇒ 传 `null`（占位键不是分类）；
   * - 真实分类 ⇒ 查得到就给 `expense` / `income`；
   * - **查不到**（不存在 / 不属于本账本）⇒ `null`。
   */
  categoryKind: CategoryKind | null;
  /** 整数分 */
  amount: number;
  /** `event` 必填（`YYYY-MM-DD`） */
  startDate: string | null;
  /** `event` 必填，闭区间最后一天 */
  endDate: string | null;
}

/**
 * 预算形态校验 —— **唯一判定处**（服务端写库前调用，端侧转发同一文案）。
 *
 * 覆盖 G 域 PRD 的 `AC-G1.3`（`event` 起止必填且不倒序）· `AC-G1.24` 的反面
 * （**只覆盖支出分类 + 总预算**，收入分类不得进预算入口 —— 决策 · §8 第 3 条）· E3（`amount > 0`）。
 *
 * @returns `null` = 通过；否则为**可直接展示的中文错误文案**
 */
export function checkBudgetShape(input: BudgetShapeInput): string | null {
  // ① 金额必须为正整数分（`AC-G1.24` 的反面 / E3）
  if (!Number.isInteger(input.amount) || input.amount <= 0) {
    return BUDGET_MESSAGES.amount;
  }

  const isTotal = input.categoryId === BudgetTotalKey;

  // ② 总预算以外的分类必须**存在**（`null` 且不是占位键 ⇒ 查不到）
  if (!isTotal && input.categoryKind === null) {
    return BUDGET_MESSAGES.unknownCategory;
  }

  // ③ 收入分类不进预算入口（规格 §5「只覆盖支出分类」）
  if (!isTotal && input.categoryKind === CategoryKind.Income) {
    return BUDGET_MESSAGES.incomeCategory;
  }

  // ④ `event` 起止必填、不得倒序（`AC-G1.3`；窗口仍按半开区间解释）
  if (input.period === BudgetPeriod.Event) {
    if (input.startDate === null || input.endDate === null) {
      return BUDGET_MESSAGES.eventRangeRequired;
    }
    if (daysBetween(input.startDate, input.endDate) < 0) {
      return BUDGET_MESSAGES.eventRangeReversed;
    }
  }

  return null;
}
